import React, { useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import ClinicvuService from 'services/Clinicvu/Clinicvu.service' // Assuming this service exists and has a method to get licenses by organization ID
// import styles from './PortalDetails.module.scss'; // We can create this later if needed
/* eslint-disable multiline-ternary */

const PortalDetails = () => {
  const { id: portalId } = useParams()
  const [licenses, setLicenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // State for the new license group form
  const [numberOfLicenses, setNumberOfLicenses] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formError, setFormError] = useState(null)

  const fetchLicenses = useCallback(async () => {
    if (!portalId) return
    setLoading(true)
    setError(null)
    try {
      // TODO: Replace 'getLicensesByOrganizationId' with the actual service method
      // and ensure it returns the licenses in the desired structure.
      // The response should be an array of license groups, where each group
      // has an expiration date and a list of licenses linked with patients.
      const response =
        await ClinicvuService.getLicensesByOrganizationId(portalId)
      setLicenses(response.data || []) // Adjust based on actual API response structure
    } catch (err) {
      console.error(`Failed to fetch licenses for portal ${portalId}:`, err)
      setError('Failed to load licenses.')
    } finally {
      setLoading(false)
    }
  }, [portalId])

  useEffect(() => {
    fetchLicenses()
  }, [fetchLicenses])

  const handleCreateLicenseGroup = async (e) => {
    e.preventDefault()
    if (!numberOfLicenses || !expiryDate) {
      setFormError('Please fill in both number of licenses and expiry date.')
      return
    }
    setIsSubmitting(true)
    setFormError(null)
    try {
      await ClinicvuService.createLicenseGroupForOrganization(
        portalId,
        parseInt(numberOfLicenses, 10),
        expiryDate
      )
      setNumberOfLicenses('')
      setExpiryDate('')
      // Re-fetch licenses to show the new group
      await fetchLicenses()
    } catch (err) {
      console.error(
        `Failed to create license group for portal ${portalId}:`,
        err
      )
      setFormError('Failed to create license group. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return <div>Loading licenses...</div>
  }

  if (error) {
    return <div>Error: {error}</div>
  }

  // Helper function to extract values from extensions
  const getExtensionValue = (extensions, url) => {
    const extension = extensions?.find((ext) => ext.url === url)
    return (
      extension?.valueDate ||
      extension?.valueInteger ||
      extension?.valueString ||
      'N/A'
    )
  }

  return (
    <div>
      <h1>Licenses for Portal {portalId}</h1>

      <h2>Create New License Group</h2>
      <form onSubmit={handleCreateLicenseGroup}>
        <div>
          <label htmlFor="numberOfLicenses">Number of Licenses: </label>
          <input
            type="number"
            id="numberOfLicenses"
            value={numberOfLicenses}
            onChange={(e) => setNumberOfLicenses(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="expiryDate">Expiry Date: </label>
          <input
            type="date"
            id="expiryDate"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            required
          />
        </div>
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create License Group'}
        </button>
        {formError && <p style={{ color: 'red' }}>{formError}</p>}
      </form>

      <hr />

      <h2>Existing Licenses</h2>
      {!licenses || licenses.length === 0 ? (
        <p>No licenses found for this portal.</p>
      ) : (
        licenses.map((group, index) => (
          <div
            key={group.id || index}
            style={{
              border: '1px solid #ccc',
              margin: '10px',
              padding: '10px'
            }}
          >
            <h3>
              License Bundle {index + 1} (ID: {group.id || 'N/A'})
            </h3>
            <p>
              <strong>Number of Licenses:</strong>{' '}
              {getExtensionValue(
                group.extension,
                'http://televu.ca/fhir/StructureDefinition/license-count'
              )}
            </p>
            <p>
              <strong>Expiry Date:</strong>{' '}
              {getExtensionValue(
                group.extension,
                'http://televu.ca/fhir/StructureDefinition/license-expiry-date'
              )}
            </p>
            {/*
                Placeholder for displaying patients linked to this license group.
                The FHIR Group resource has a 'member' array for this.
                Each member object would typically have an 'entity' field with a reference like 'Patient/patient-id'.
              */}
            <h4>Associated Patients:</h4>
            {group.member && group.member.length > 0 ? (
              <ul>
                {group.member.map((member, memberIndex) => (
                  <li key={memberIndex}>
                    {member.entity?.reference || 'Unknown Patient'}
                    {/* You might want to fetch patient details based on the reference */}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No patients directly associated with this license bundle.</p>
            )}
          </div>
        ))
      )}
    </div>
  )
}

export default PortalDetails
